if (!customElements.get('sticky-buy-bar')) {
  customElements.define(
    'sticky-buy-bar',
    class StickyBuyBar extends HTMLElement {
      constructor() {
        super();
        this.productInfo = Array.from(document.querySelectorAll('product-info')).find(
          (el) => !el.closest('quick-add-modal')
        );
        this.form = this.productInfo?.querySelector('form[data-type="add-to-cart-form"]');
        this.submitButton = this.productInfo?.querySelector('[id^="ProductSubmitButton-"]');
        this.priceElement = this.productInfo?.querySelector('[id^="price-"]');
        this.sectionId = this.productInfo?.dataset.section;
        this.priceWrapper = this.querySelector('.sticky-buy-bar__price');
        this.variantTextEl = this.querySelector('[data-sticky-variant-text]');
        this.optionNames = (this.dataset.optionNames || '').split(',').map((name) => name.trim());
        this.cartButton = this.querySelector('[data-sticky-action="add-to-cart"]');
        this.cartButtonSpinner = this.cartButton?.querySelector('.loading__spinner');
        this.cartCountBadge = this.querySelector('[data-sticky-cart-count]');
        this.cartCountValue = this.querySelector('[data-sticky-cart-count-value]');
        this.headerCartIcon = document.getElementById('cart-icon-bubble');
      }

      connectedCallback() {
        if (!this.form || !this.submitButton) return;

        this.syncPrice();
        this.setDisabled(this.submitButton.hasAttribute('disabled'));

        this.observer = new IntersectionObserver(this.handleIntersection.bind(this), { threshold: 0 });
        this.observer.observe(this.submitButton);

        this.cartButton?.addEventListener('click', () => this.addToCart());

        // All subscriptions below run on the theme's shared pub/sub bus, which product-form.js also
        // relies on to open the cart drawer/notification after adding to cart. Every callback is
        // wrapped in safely() so a problem here can never throw and block that unrelated code.
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) =>
          this.safely(() => this.handleVariantChange(event))
        );
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () =>
          this.safely(() => this.setLoading(false))
        );
        this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, () =>
          this.safely(() => this.setLoading(false))
        );

        // Cart count mirrors the header's own icon via MutationObserver instead of the pub/sub bus,
        // so it can never interfere with the shared add-to-cart flow.
        if (this.headerCartIcon) {
          this.safely(() => this.syncCartCount());
          this.cartIconObserver = new MutationObserver(() => this.safely(() => this.syncCartCount()));
          this.cartIconObserver.observe(this.headerCartIcon, { childList: true, subtree: true, characterData: true });
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.variantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.cartErrorUnsubscriber?.();
        this.cartIconObserver?.disconnect();
      }

      safely(fn) {
        try {
          fn();
        } catch (error) {
          console.error('[sticky-buy-bar]', error);
        }
      }

      handleIntersection(entries) {
        entries.forEach((entry) => {
          this.classList.toggle(
            'sticky-buy-bar--visible',
            !entry.isIntersecting && entry.boundingClientRect.top < 0
          );
        });
      }

      handleVariantChange(event) {
        const sectionId = event?.data?.sectionId;
        const html = event?.data?.html;
        if (!html || sectionId !== this.sectionId) return;

        const newPrice = this.priceElement?.id ? html.getElementById(this.priceElement.id) : null;
        if (newPrice && this.priceWrapper) this.priceWrapper.innerHTML = newPrice.innerHTML;

        this.updateVariantText(event.data.variant);

        const submitId = this.submitButton?.id;
        const disabled = submitId ? (html.getElementById(submitId)?.hasAttribute('disabled') ?? true) : true;
        this.setDisabled(disabled);
      }

      updateVariantText(variant) {
        if (!this.variantTextEl || !variant?.options) return;

        const colorIndex = this.optionNames.findIndex((name) => /^colou?r$/i.test(name));
        const sizeIndex = this.optionNames.findIndex((name) => /^size$/i.test(name));

        const parts = [];
        if (colorIndex > -1 && variant.options[colorIndex]) parts.push(variant.options[colorIndex]);
        if (sizeIndex > -1 && variant.options[sizeIndex]) parts.push(variant.options[sizeIndex]);

        this.variantTextEl.textContent = parts.join(' / ');
      }

      syncPrice() {
        if (this.priceElement && this.priceWrapper) this.priceWrapper.innerHTML = this.priceElement.innerHTML;
      }

      setDisabled(disabled) {
        if (this.cartButton) this.cartButton.disabled = disabled;
      }

      setLoading(isLoading) {
        if (!this.cartButton) return;
        this.cartButton.classList.toggle('loading', isLoading);
        this.cartButton.toggleAttribute('aria-disabled', isLoading);
        this.cartButtonSpinner?.classList.toggle('hidden', !isLoading);
      }

      addToCart() {
        if (!this.form || this.cartButton?.getAttribute('aria-disabled') === 'true') return;
        // Gives immediate visual feedback: the real submit button's own spinner is off-screen
        // behind this bar, so without this the tap otherwise looks like it did nothing for a
        // moment while the request is in flight.
        this.setLoading(true);
        // Reuses the real add-to-cart form so the existing cart drawer/notification behaves exactly as normal.
        this.form.requestSubmit();
      }

      syncCartCount() {
        if (!this.cartCountBadge) return;
        const bubble = this.headerCartIcon?.querySelector('.cart-count-bubble');
        const value = bubble?.querySelector('[aria-hidden="true"]')?.textContent;
        this.cartCountBadge.hidden = !bubble;
        if (this.cartCountValue) this.cartCountValue.textContent = value ?? '';
      }
    }
  );
}
